import { EmailAuthProvider, GoogleAuthProvider, reauthenticateWithCredential, reauthenticateWithPopup, signOut, type User } from "firebase/auth";
import { Eye, EyeOff, Fingerprint, KeyRound, LockKeyhole, LogOut } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { friendlyError } from "../lib/errors";
import { auth } from "../lib/firebase";
import { unlockWithPasskey } from "../services/auth";
import { Brand } from "../components/Brand";
import { DEFAULT_QUICK_LOCK_SETTINGS, QuickLockContext, type QuickLockSettings } from "./QuickLockContext";

const LOCKED_KEY = "dt:quick-lock:locked";

function settingsKey(uid: string) {
  return `dt:quick-lock:settings:${uid}`;
}

function loadSettings(uid: string): QuickLockSettings {
  try {
    const value = JSON.parse(localStorage.getItem(settingsKey(uid)) ?? "null") as Partial<QuickLockSettings> | null;
    const timeoutMinutes = [0, 1, 5, 15, 30].includes(Number(value?.timeoutMinutes)) ? Number(value?.timeoutMinutes) : DEFAULT_QUICK_LOCK_SETTINGS.timeoutMinutes;
    return { timeoutMinutes, lockOnHide: value?.lockOnHide === true };
  } catch {
    return DEFAULT_QUICK_LOCK_SETTINGS;
  }
}

export function QuickLockProvider({ user, children }: { user: User; children: ReactNode }) {
  const [locked, setLocked] = useState(() => sessionStorage.getItem(LOCKED_KEY) === "true");
  const [settings, setSettings] = useState(() => loadSettings(user.uid));
  const timer = useRef<number | null>(null);

  const lock = useCallback(() => {
    sessionStorage.setItem(LOCKED_KEY, "true");
    setLocked(true);
  }, []);

  const unlock = useCallback(() => {
    sessionStorage.removeItem(LOCKED_KEY);
    setLocked(false);
  }, []);

  const updateSettings = useCallback((next: QuickLockSettings) => {
    localStorage.setItem(settingsKey(user.uid), JSON.stringify(next));
    setSettings(next);
  }, [user.uid]);

  useEffect(() => {
    if (locked || settings.timeoutMinutes === 0) return;
    const reset = () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(lock, settings.timeoutMinutes * 60_000);
    };
    const events: Array<keyof WindowEventMap> = ["pointerdown", "keydown", "touchstart"];
    events.forEach((event) => window.addEventListener(event, reset, { passive: true }));
    reset();
    return () => {
      events.forEach((event) => window.removeEventListener(event, reset));
      if (timer.current !== null) window.clearTimeout(timer.current);
    };
  }, [lock, locked, settings.timeoutMinutes]);

  useEffect(() => {
    if (!settings.lockOnHide || locked) return;
    const onVisibilityChange = () => { if (document.visibilityState === "hidden") lock(); };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [lock, locked, settings.lockOnHide]);

  const value = useMemo(() => ({ locked, settings, lock, unlock, updateSettings }), [locked, settings, lock, unlock, updateSettings]);
  return <QuickLockContext.Provider value={value}>{locked ? <QuickLockScreen user={user} onUnlock={unlock} /> : children}</QuickLockContext.Provider>;
}

function QuickLockScreen({ user, onUnlock }: { user: User; onUnlock: () => void }) {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const providers = new Set(user.providerData.map((provider) => provider.providerId));
  const hasPassword = providers.has("password") && Boolean(user.email);
  const hasGoogle = providers.has("google.com");

  async function run(action: () => Promise<unknown>) {
    setBusy(true); setError("");
    try { await action(); onUnlock(); }
    catch (reason) { setError(friendlyError(reason)); }
    finally { setBusy(false); }
  }

  return <main className="quick-lock-screen">
    <section className="quick-lock-card" aria-labelledby="quick-lock-title">
      <Brand />
      <div className="quick-lock-icon"><LockKeyhole /></div>
      <p className="eyebrow">Private content hidden</p>
      <h1 id="quick-lock-title">DickerTicker is locked.</h1>
      <p>Verify it is you to reopen this private session.</p>
      {error ? <p className="form-message form-message--error" role="alert">{error}</p> : null}
      <button className="button button--primary button--full" disabled={busy} onClick={() => run(() => unlockWithPasskey(user.uid))}><Fingerprint /> Unlock with passkey</button>
      {hasPassword ? <form className="form-stack" onSubmit={(event) => { event.preventDefault(); void run(() => reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email!, password))); }}>
        <label><span>Password</span><div className="input-with-icon"><KeyRound /><input type={showPassword ? "text" : "password"} value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="current-password" required /><button type="button" className="input-action" onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? "Hide password" : "Show password"}>{showPassword ? <EyeOff /> : <Eye />}</button></div></label>
        <button className="button button--secondary button--full" disabled={busy || !password}>Unlock with password</button>
      </form> : null}
      {hasGoogle ? <button className="button button--secondary button--full" disabled={busy} onClick={() => run(() => reauthenticateWithPopup(user, new GoogleAuthProvider()))}>Continue with Google</button> : null}
      <button className="text-button quick-lock-signout" disabled={busy} onClick={() => { sessionStorage.removeItem(LOCKED_KEY); void signOut(auth); }}><LogOut /> Sign out instead</button>
    </section>
  </main>;
}
